import React, { useMemo, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useHistory } from 'react-router';
import AppContext from '../../context/AppContext';
import useTitle from '../../util/useTitle';
import InternalLayout from '../layouts/InternalLayout';
import WORKPAGE_COMPONENTS from './fields';
import { useIsMobile } from 'src/hooks/useIsMobile';
import classnames from 'src/util/classnames';
import { localizeWorkpageElement } from 'src/util/workpageI18n';
import { OcifExtensionsContextProvider } from '../../context/OcifExtensionsContext';
import WorkpageTestIdContext from '../../context/WorkpageTestIdContext';

const SHRINK_TO_CONTENT_CLASS = 'grid-row-shrink-content';

// Which row (0-based) each element lands in, using the same col/colSpan wrapping rules the render
// below uses to place elements. Needed so grid-row-shrink-content can split the page's height
// between rows without assuming a fixed row count.
function computeRowIndices(elements, gridSize) {
  let lastColEnd = 1;
  let row = -1;

  return (elements || []).map(element => {
    let { col = lastColEnd, colSpan = gridSize } = element;
    colSpan = Math.min(colSpan, gridSize);
    let colEnd = col + colSpan;

    if (colEnd > gridSize + 1) {
      col = 1;
      colEnd = col + colSpan;
    }

    if (col === 1) row += 1;
    lastColEnd = ((colEnd - 1) % gridSize) + 1;

    return row;
  });
}

// Water-fills `available` across rows: any row whose content fits within an equal share of what's
// left gets exactly its content height, and the space it didn't use is re-split among the rows
// still overflowing. Repeats until every remaining row overflows its share, then caps those at it.
// So a short worktray yields its slack to a crowded cardtray instead of both getting the same
// fixed fraction of the page.
function distributeRowHeights(rowNeeds, available) {
  const heights = new Array(rowNeeds.length);
  let open = rowNeeds.map((_, row) => row);
  let remaining = Math.max(available, 0);

  while (open.length) {
    const share = remaining / open.length;
    const fitting = open.filter(row => rowNeeds[row] <= share);

    if (!fitting.length) {
      open.forEach(row => {
        heights[row] = share;
      });
      break;
    }

    fitting.forEach(row => {
      heights[row] = rowNeeds[row];
      remaining -= rowNeeds[row];
    });
    open = open.filter(row => rowNeeds[row] > share);
  }

  return heights;
}

// See the grid-row-shrink-content comment in _gridTypes.css.js for why the row cap is applied in
// JS as an explicit pixel height rather than in CSS.
//
// Every applyHeights() call resets a widget's height to auto before measuring it: once a height
// is pinned, every height: 100% descendant becomes fixed too, so a nested overflow:auto container
// (e.g. .worktray-table-container) starts actively clipping — and clipped content never counts
// towards an ancestor's scrollHeight again. Without the reset, a widget measured while sparse
// (e.g. before an async fetch resolves) would stay stuck at that size forever, even after enough
// rows arrive to need more space — the growth would just get silently absorbed by that inner
// scrollbar instead of reported back up.
//
// Watching for that growth can't lean on ResizeObserver on the widget itself, for the same
// reason: while pinned, its box does not change size as content grows. A MutationObserver on its
// subtree instead re-runs the measurement whenever the DOM actually changes (e.g. rows arriving),
// regardless of what's rendered inside (worktray, cardtray, or anything else).
function useShrinkToContentRowHeights(enabled, containerRef, widgetRefs, rowIndices) {
  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const rowCount = rowIndices.length ? Math.max(...rowIndices) + 1 : 1;

    const applyHeights = () => {
      const style = getComputedStyle(container);
      const available =
        container.clientHeight -
        (parseFloat(style.paddingTop) || 0) -
        (parseFloat(style.paddingBottom) || 0) -
        (parseFloat(style.rowGap) || 0) * (rowCount - 1);
      const fairShare = available / rowCount;

      // WorkTray marks its container `full-height` while loading or genuinely empty, and CardTray
      // while loading (see WorkTray.tsx / CardTray.tsx), since those states' content doesn't
      // reflect the eventual row count. Sizing to
      // content in that window would shrink the widget down to a spinner/empty-state's height,
      // then jump once real rows replace it — so it claims its full fair share instead, and only
      // sizes to content once WorkTray itself reports it has something to measure.
      const widgetNeeds = widgetRefs.current.map(widget => {
        if (!widget) return 0;
        if (widget.querySelector('.full-height')) return fairShare;
        widget.style.height = 'auto';
        return widget.scrollHeight;
      });

      const rowNeeds = new Array(rowCount).fill(0);
      widgetNeeds.forEach((need, idx) => {
        const row = rowIndices[idx];
        if (row !== undefined) rowNeeds[row] = Math.max(rowNeeds[row], need);
      });

      const rowHeights = distributeRowHeights(rowNeeds, available);

      widgetRefs.current.forEach((widget, idx) => {
        if (!widget) return;
        const rowHeight = rowHeights[rowIndices[idx]] ?? fairShare;
        widget.style.height = `${Math.min(widgetNeeds[idx], rowHeight)}px`;
      });
    };

    applyHeights();

    const resizeObserver = new ResizeObserver(applyHeights);
    resizeObserver.observe(container);

    const mutationObservers = widgetRefs.current.map(widget => {
      if (!widget) return null;
      const observer = new MutationObserver(applyHeights);
      observer.observe(widget, { childList: true, subtree: true });
      return observer;
    });

    return () => {
      resizeObserver.disconnect();
      mutationObservers.forEach(observer => observer?.disconnect());
    };
  }, [enabled, containerRef, widgetRefs, rowIndices]);
}

function Workpage({ schema, scope, inForm = false, slug }) {
  const isMobile = useIsMobile();
  const { i18n } = useLingui();
  const { title: propTitle, name: propName, elements: rawElements, columns, ':classNames': classNames } = schema || {};
  const testIdScope = slug || scope?.workpage?.slug || scope?.workpage?.name || '';

  const elements = useMemo(() => rawElements?.map(element => localizeWorkpageElement(element, i18n.locale)), [rawElements, i18n.locale]);

  const title = useMemo(() => {
    if (propTitle) return propTitle;
    if (propName) return name;
    return (elements.find(e => e.title && !e.ignoreTitle) || {}).title ?? t`Workpage`;
  }, [propTitle, elements, propName]);

  useTitle(inForm ? '' : title);

  const history = useHistory();
  const grid_size = columns ?? 4;

  const [workpageData, _setWorkpageData] = useState({});
  const setWorkpageData = useCallback(
    (name, value) => {
      _setWorkpageData(dd => ({ ...dd, [name]: value }));
    },
    [_setWorkpageData]
  );

  if (typeof scope !== 'undefined') {
    scope.setworkpageData = setWorkpageData;
    scope.workpageData = workpageData;
  }

  const isShrinkToContent = (classNames || '').split(/\s+/).includes(SHRINK_TO_CONTENT_CLASS);
  const containerRef = useRef(null);
  const widgetRefs = useRef([]);
  const rowIndices = useMemo(() => computeRowIndices(elements, grid_size), [elements, grid_size]);

  useShrinkToContentRowHeights(isShrinkToContent, containerRef, widgetRefs, rowIndices);

  return elements?.length ? (
    <WorkpageTestIdContext.Provider value={testIdScope}>
      <div ref={containerRef} className={classnames('workpage px-4', classNames, { 'mobile-workpage': isMobile })}>
        {
          elements.reduce(
            (_, element, idx) => {
              let { col = _.lastColEnd, colSpan = grid_size } = element;
              colSpan = Math.min(colSpan, grid_size);
              let colEnd = col + colSpan;

              if (colEnd > grid_size + 1) {
                col = 1;
                colEnd = col + colSpan;
              }

              _.lastColEnd = ((colEnd - 1) % grid_size) + 1;

              const Component = WORKPAGE_COMPONENTS[element.type];
              // Namespaces this element's own query-string params (pagination,
              // sorting, etc — see useSearchParams) so two elements of the same
              // type in one workpage (e.g. two Worktrays) don't read/write the
              // same URL keys and stomp on each other's state.
              const elementId = element.name || `el${idx}`;

              _.list.push(
                <div
                  key={idx}
                  ref={el => {
                    widgetRefs.current[idx] = el;
                  }}
                  className={`workpage-widget from-col-${col} to-col-${colEnd}`}
                >
                  {Component ? <Component element={element} history={history} scope={scope} inForm={inForm} elementId={elementId} /> : null}
                </div>
              );

              return _;
            },
            { lastColEnd: 1, list: [] }
          ).list
        }
      </div>
    </WorkpageTestIdContext.Provider>
  ) : null;
}

const WRAPPERS = {
  internal: (workpage, workpageDef) => (
    <InternalLayout>
      {workpageDef.tab ? <AppContext.SetSidebarTab value={workpageDef.tab} /> : null}
      <OcifExtensionsContextProvider>{workpage}</OcifExtensionsContextProvider>
    </InternalLayout>
  ),
};

Workpage.HOC = function(workpageDef, defaultScope) {
  const { name = 'Workpage', wrapperType, wrapper, schema } = workpageDef;
  const schemaFn = typeof schema === 'function' ? schema : () => schema;
  const wrapperFn = wrapper || WRAPPERS[wrapperType] || (children => children);

  const scope = {
    ...(defaultScope ?? {}),
    workpage: workpageDef,
  };

  function HOC() {
    const schema = useMemo(schemaFn, [schemaFn]);

    return wrapperFn(<Workpage schema={schema} scope={scope} slug={workpageDef.slug || workpageDef.name} />, workpageDef);
  }

  HOC.DisplayName = name || 'WorkpageHOC';

  return HOC;
};

export default Workpage;
